export enum Locale {
  fiFI = 'fi-FI',
  svSE = 'sv-SE',
  daDK = 'da-DK',
  enGB = 'en-GB',
}

export enum Legislation {
  FI = 'FI',
  SE = 'SE',
  DK = 'DK',
  GB_ENG = 'GB-ENG',
  GB_WLS = 'GB-WLS',
  GLOBAL = 'GLOBAL',
}

export const getLegislationFromLocale = (locale: Locale): Legislation => {
  switch (locale) {
    case Locale.fiFI:
      return Legislation.FI;
    case Locale.svSE:
      return Legislation.SE;
    case Locale.daDK:
      return Legislation.DK;
    case Locale.enGB:
      return Legislation.GB_ENG;
    default:
      throw new Error(`No legislation found for locale ${locale}`);
  }
};

export const currencies = ['€', 'SEK', 'DKK', '£', '$'] as const;
export type Currency = (typeof currencies)[number];

export const countryCodes = ['fi', 'se', 'dk', 'gb', 'global'] as const;
export type CountryCode = (typeof countryCodes)[number];

const langKeys = ['fi', 'sv', 'da', 'en'] as const;
export type LangKey = (typeof langKeys)[number];

const countryCodeToLocaleMap: Record<CountryCode, Locale> = {
  fi: Locale.fiFI,
  se: Locale.svSE,
  dk: Locale.daDK,
  gb: Locale.enGB,
  global: Locale.enGB,
};

const localeToCountryCodeMap: Record<Locale, CountryCode> = {
  [Locale.fiFI]: 'fi',
  [Locale.svSE]: 'se',
  [Locale.daDK]: 'dk',
  [Locale.enGB]: 'gb',
};

const localeToLangMap: Record<Locale, LangKey> = {
  [Locale.fiFI]: 'fi',
  [Locale.svSE]: 'sv',
  [Locale.daDK]: 'da',
  [Locale.enGB]: 'en',
};

const countryCodeToCurrencyMap: Record<CountryCode, Currency> = {
  fi: '€',
  se: 'SEK',
  dk: 'DKK',
  gb: '£',
  global: '$',
};

export const localeToLang = (locale: Locale): LangKey => localeToLangMap[locale];
export const countryCodeToLocale = (countryCode: CountryCode) =>
  countryCodeToLocaleMap[countryCode];
export const localeToCountryCode = (locale: Locale): CountryCode => localeToCountryCodeMap[locale];
export const countryCodeToCurrency = (countryCode: CountryCode): Currency =>
  countryCodeToCurrencyMap[countryCode];

export const isCountryCode = (maybeCountryCode: string): maybeCountryCode is CountryCode =>
  countryCodes.includes(maybeCountryCode as any); // cast to any to allow comparison between string and string literals

/** check the first part of the path if it maches a country code, e.g /se/ or /dk/ and if those don't exist default to 'fi' */
export const extractCountryCodeFromPath = (path?: string): CountryCode => {
  const maybeCountryCode = path?.split('/')?.[1];
  if (maybeCountryCode === 'uk') {
    return 'gb';
  }
  if (maybeCountryCode && isCountryCode(maybeCountryCode)) {
    return maybeCountryCode;
  } else {
    return 'gb';
  }
};

export const getPageLocaleFromPath = (path?: string): Locale => {
  const countryCode = extractCountryCodeFromPath(path);
  return countryCodeToLocale(countryCode);
};
